import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setNodes,
  setSelectedNode,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addNode,
  updateNode as updateNodeAction,
  removeNode,
  clearSelectedNode,
  resetState,
} from '../../store/slices/decisionNodesSlice';
import {
  GET_DECISION_NODES_LIST,
  GET_DECISION_NODE,
} from '../../graphql/queries/decisionNodes.queries';
import {
  CREATE_DECISION_NODE,
  UPDATE_DECISION_NODE,
  DELETE_DECISION_NODE,
} from '../../graphql/mutations/decisionNodes.mutations';
import {
  FindDecisionNodeListInput,
  FindDecisionNodeInput,
  CreateDecisionNodeInput,
  UpdateDecisionNodeInput,
  DeleteDecisionNodeInput,
  DecisionNodeListResponse,
  DecisionNodeResponse,
  DecisionNodeMutationResponse,
  DeleteDecisionNodeResponse,
  DecisionNode,
} from '../../types/decisionNodes.types';
import ViewDecisionNodeModal from '../../components/DecisionNodes/ViewDecisionNodeModal';
import CreateEditDecisionNodeModal, {
  DecisionNodeFormData,
} from '../../components/DecisionNodes/CreateEditDecisionNodeModal';
import ConfirmDeleteDecisionNodeDialog from '../../components/DecisionNodes/ConfirmDeleteDecisionNodeDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './DecisionNodes.css';

const DecisionNodes: React.FC = () => {
  const navigate = useNavigate();
  const { treeKey } = useParams<{ treeKey: string }>();
  const dispatch = useAppDispatch();
  const { nodes, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.decisionNodes
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<DecisionNode | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchNodes, { loading: fetchingList }] = useLazyQuery<
    { decisionNodes: DecisionNodeListResponse },
    { params: FindDecisionNodeListInput }
  >(GET_DECISION_NODES_LIST, { fetchPolicy: 'network-only' });

  const [fetchNode] = useLazyQuery<
    { decisionNode: DecisionNodeResponse },
    { params: FindDecisionNodeInput }
  >(GET_DECISION_NODE, { fetchPolicy: 'network-only' });

  const [createNodeMutation] = useMutation<
    { createDecisionNode: DecisionNodeMutationResponse },
    { params: CreateDecisionNodeInput }
  >(CREATE_DECISION_NODE);

  const [updateNodeMutation] = useMutation<
    { updateDecisionNode: DecisionNodeMutationResponse },
    { params: UpdateDecisionNodeInput }
  >(UPDATE_DECISION_NODE);

  const [deleteNodeMutation] = useMutation<
    { deleteDecisionNode: DeleteDecisionNodeResponse },
    { params: DeleteDecisionNodeInput }
  >(DELETE_DECISION_NODE);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    if (treeKey) {
      loadNodes();
    }
  }, [pagination.page, pagination.pageSize, search, sort, treeKey]);

  const loadNodes = async () => {
    if (!treeKey) return;
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNodes({
        variables: {
          params: {
            treeKey,
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.decisionNodes?.success && data.decisionNodes.data) {
        dispatch(setNodes(data.decisionNodes.data));
      } else {
        dispatch(setError(data?.decisionNodes?.message || 'Error al cargar nodos'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'asc' }));
    } else {
      dispatch(setSort({ field: 'displayOrder', order: 'asc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNode({ variables: { params: { ids: [id] } } });
      if (data?.decisionNode?.success && data.decisionNode.data && data.decisionNode.data.length > 0) {
        dispatch(setSelectedNode(data.decisionNode.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.decisionNode?.message || 'Error al cargar nodo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNode({ variables: { params: { ids: [id] } } });
      if (data?.decisionNode?.success && data.decisionNode.data && data.decisionNode.data.length > 0) {
        dispatch(setSelectedNode(data.decisionNode.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.decisionNode?.message || 'Error al cargar nodo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedNode());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: DecisionNodeFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateNodeMutation({
          variables: {
            params: {
              id: formData.id,
              treeKey: formData.treeKey,
              nodeKey: formData.nodeKey,
              nodeType: formData.nodeType,
              messageTemplate: formData.messageTemplate,
              options: formData.options,
              inputConfig: formData.inputConfig,
              dynamicOptionsConfig: formData.dynamicOptionsConfig,
              terminalConfig: formData.terminalConfig,
              contextUpdates: formData.contextUpdates,
              parentNodeKey: formData.parentNodeKey,
              nextNode: formData.nextNode,
              nextNodeOnSelect: formData.nextNodeOnSelect,
              requiresTenant: formData.requiresTenant,
              displayOrder: formData.displayOrder,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.updateDecisionNode?.success) {
          if (responseData.updateDecisionNode.data) {
            dispatch(updateNodeAction(responseData.updateDecisionNode.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedNode());
          setSaveError(null);
          setSuccessMessage('Nodo de decisión actualizado exitosamente');
        } else {
          const msg = responseData?.updateDecisionNode?.message || 'Error al actualizar nodo';
          const errs = responseData?.updateDecisionNode?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createNodeMutation({
          variables: {
            params: {
              treeKey: formData.treeKey,
              nodeKey: formData.nodeKey,
              nodeType: formData.nodeType,
              messageTemplate: formData.messageTemplate,
              options: formData.options,
              inputConfig: formData.inputConfig,
              dynamicOptionsConfig: formData.dynamicOptionsConfig,
              terminalConfig: formData.terminalConfig,
              contextUpdates: formData.contextUpdates,
              parentNodeKey: formData.parentNodeKey,
              nextNode: formData.nextNode,
              nextNodeOnSelect: formData.nextNodeOnSelect,
              requiresTenant: formData.requiresTenant,
              displayOrder: formData.displayOrder,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.createDecisionNode?.success) {
          if (responseData.createDecisionNode.data) {
            dispatch(addNode(responseData.createDecisionNode.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedNode());
          setSaveError(null);
          setSuccessMessage('Nodo de decisión creado exitosamente');
        } else {
          const msg = responseData?.createDecisionNode?.message || 'Error al crear nodo';
          const errs = responseData?.createDecisionNode?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (node: DecisionNode) => {
    setNodeToDelete(node);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!nodeToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteNodeMutation({
        variables: {
          params: {
            ids: [nodeToDelete.id],
          },
        },
      });

      if (responseData?.deleteDecisionNode?.success) {
        dispatch(removeNode(nodeToDelete.id));
        setDeleteDialogOpen(false);
        setNodeToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Nodo de decisión eliminado exitosamente');
      } else {
        const msg = responseData?.deleteDecisionNode?.message || 'Error al eliminar nodo';
        const errs = responseData?.deleteDecisionNode?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedNode());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedNode());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setNodeToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<DecisionNode>[] = [
    { field: 'nodeKey', headerName: 'Clave del Nodo', width: 180 },
    { field: 'nodeType', headerName: 'Tipo', width: 150 },
    {
      field: 'messageTemplate',
      headerName: 'Plantilla de Mensaje',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string | null) => value || '',
    },
    { field: 'parentNodeKey', headerName: 'Nodo Padre', width: 150, valueGetter: (value: string | null) => value || '' },
    { field: 'displayOrder', headerName: 'Orden', width: 90 },
    {
      field: 'isActive',
      headerName: 'Activo',
      width: 90,
      valueGetter: (value: boolean) => value ? 'Sí' : 'No',
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="decision-nodes-container">
        <Paper elevation={2} className="decision-nodes-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Link
              underline="hover"
              sx={{ cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/decision-trees')}
            >
              Árbol de Decisiones
            </Link>
            <Typography color="text.primary">Nodos - {treeKey}</Typography>
          </Breadcrumbs>

          <Box className="decision-nodes-header">
            <Typography variant="h4" component="h1">
              Nodos de Decisión
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nodos del árbol: <strong>{treeKey}</strong>
            </Typography>
          </Box>

          <Box className="decision-nodes-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar nodo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Nodo
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={nodes}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewDecisionNodeModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditDecisionNodeModal
          open={createEditModalOpen}
          isEdit={isEdit}
          treeKey={treeKey || ''}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteDecisionNodeDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          nodeKey={nodeToDelete?.nodeKey || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default DecisionNodes;
